import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import {
  ArrowRight,
  BookOpen,
  Check,
  CheckCircle2,
  ChevronRight,
  FileText,
  Layers,
  MessageSquare,
  Play,
  Shield,
  Sparkles,
  Terminal,
  User,
  Zap,
} from 'lucide-react';
import clsx from 'clsx';

/* ------------------------------------------------------------------ */
/*  Utility: fade-in-on-scroll wrapper                                */
/* ------------------------------------------------------------------ */
function FadeIn({
  children,
  className,
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Chat bubbles — right-aligned developer, left-aligned Claude        */
/* ------------------------------------------------------------------ */
function DeveloperMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[85%]">
        <div className="flex items-center gap-1.5 justify-end mb-1">
          <span className="text-xs font-medium text-gray-500">Developer</span>
          <User className="h-3 w-3 text-gray-500" />
        </div>
        <div className="rounded-2xl rounded-tr-sm bg-surface-100 border border-dark-border px-4 py-3">
          <div className="text-sm text-gray-200">{children}</div>
        </div>
      </div>
    </div>
  );
}

function ClaudeMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex justify-start">
      <div className="max-w-[85%]">
        <div className="flex items-center gap-1.5 mb-1">
          <Sparkles className="h-3 w-3 text-accent" />
          <span className="text-xs font-medium text-accent">Claude Code</span>
        </div>
        <div className="rounded-2xl rounded-tl-sm bg-accent/5 border border-accent/20 px-4 py-3">
          <div className="text-sm text-gray-300">{children}</div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Guardrail callout                                                  */
/* ------------------------------------------------------------------ */
function GuardrailCallout({
  title,
  description,
  without,
  color = 'accent',
}: {
  title: string;
  description: string;
  without: string;
  color?: 'accent' | 'green' | 'amber' | 'red' | 'purple';
}) {
  const colors = {
    accent: 'border-accent/30 bg-accent/5',
    green: 'border-green-500/30 bg-green-500/5',
    amber: 'border-amber-500/30 bg-amber-500/5',
    red: 'border-red-500/30 bg-red-500/5',
    purple: 'border-purple-500/30 bg-purple-500/5',
  };
  const iconColors = {
    accent: 'text-accent',
    green: 'text-green-400',
    amber: 'text-amber-400',
    red: 'text-red-400',
    purple: 'text-purple-400',
  };

  return (
    <div className={clsx('rounded-lg border p-4 mt-4', colors[color])}>
      <div className="flex items-start gap-3">
        <Shield className={clsx('h-4 w-4 mt-0.5 shrink-0', iconColors[color])} />
        <div>
          <h4 className="text-sm font-semibold text-white mb-1">{title}</h4>
          <p className="text-xs text-gray-400 mb-2">{description}</p>
          <p className="text-xs text-gray-500 italic">
            Without guardrails: {without}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Step card — wraps developer input + claude response + guardrail    */
/* ------------------------------------------------------------------ */
function StepCard({
  stepNumber,
  title,
  developerSays,
  claudeResponse,
  guardrail,
  className,
}: {
  stepNumber: number;
  title: string;
  developerSays: string;
  claudeResponse: React.ReactNode;
  guardrail: {
    title: string;
    description: string;
    without: string;
    color?: 'accent' | 'green' | 'amber' | 'red' | 'purple';
  };
  className?: string;
}) {
  return (
    <FadeIn className={clsx('mb-12', className)}>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-8 h-8 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center text-xs font-bold text-accent">
          {stepNumber}
        </div>
        <h3 className="text-lg font-semibold text-white">{title}</h3>
      </div>

      <div className="space-y-4 mb-4">
        <DeveloperMessage>{developerSays}</DeveloperMessage>
        <ClaudeMessage>{claudeResponse}</ClaudeMessage>
      </div>

      <GuardrailCallout {...guardrail} />
    </FadeIn>
  );
}

/* ------------------------------------------------------------------ */
/*  Act header                                                         */
/* ------------------------------------------------------------------ */
function ActHeader({
  actNumber,
  title,
  description,
  icon: Icon,
}: {
  actNumber: number;
  title: string;
  description: string;
  icon: React.ElementType;
}) {
  return (
    <FadeIn className="mb-12 mt-24 first:mt-0">
      <div className="flex items-center gap-4 mb-3">
        <div className="w-12 h-12 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center">
          <Icon className="h-6 w-6 text-accent" />
        </div>
        <div>
          <div className="text-xs font-medium text-accent mb-1">Act {actNumber}</div>
          <h2 className="text-2xl font-bold text-white">{title}</h2>
        </div>
      </div>
      <p className="text-gray-400 ml-16">{description}</p>
      <div className="mt-6 border-t border-dark-border" />
    </FadeIn>
  );
}

/* ------------------------------------------------------------------ */
/*  Hero Header                                                        */
/* ------------------------------------------------------------------ */
function HeroHeader() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="text-center mb-20"
    >
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 border border-accent/20 text-accent text-sm font-medium mb-6">
        <Play className="h-3.5 w-3.5" />
        Live Session Recording
      </div>
      <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-4">
        A Developer&apos;s <span className="text-gradient">First Day</span>
      </h1>
      <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-6">
        Watch what happens when a developer opens a CodeForge-generated project for the
        first time. Every response is shaped by guardrails — not luck.
      </p>
      <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
        <div className="flex items-center gap-2">
          <Terminal className="h-4 w-4" />
          <span>Claude Code 2.1</span>
        </div>
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4" />
          <span>Greenfield Playbook</span>
        </div>
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          <span className="text-green-400">11/11 steps passed</span>
        </div>
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Session context banner                                             */
/* ------------------------------------------------------------------ */
function SessionContext() {
  return (
    <FadeIn>
      <div className="card border-accent/20 mb-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <div className="text-xs text-gray-500 mb-1">Project</div>
            <div className="text-sm font-semibold text-white">TaskFlow</div>
            <div className="text-xs text-gray-400">Task management for remote teams</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">Tech Stack</div>
            <div className="text-sm font-semibold text-white">TypeScript + Next.js + PostgreSQL</div>
            <div className="text-xs text-gray-400">Vitest, GitHub Actions, AWS</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">Team</div>
            <div className="text-sm font-semibold text-white">3 developers, 4-month timeline</div>
            <div className="text-xs text-gray-400">SOC2 + GDPR compliance</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">Playbook</div>
            <div className="text-sm font-semibold text-white">Greenfield Development</div>
            <div className="text-xs text-gray-400">7 phases, 21 prompt templates</div>
          </div>
        </div>
      </div>
    </FadeIn>
  );
}

/* ------------------------------------------------------------------ */
/*  Scoreboard                                                         */
/* ------------------------------------------------------------------ */
function Scoreboard() {
  const steps = [
    { num: 1, name: 'Project orientation', pass: true },
    { num: 2, name: 'Command discovery', pass: true },
    { num: 3, name: 'Phase status', pass: true },
    { num: 4, name: 'Phase guidance', pass: true },
    { num: 5, name: 'Requirements generation', pass: true },
    { num: 6, name: 'User stories', pass: true },
    { num: 7, name: 'NFR checklist', pass: true },
    { num: 8, name: 'Quality gate check', pass: true },
    { num: 9, name: 'Phase advance', pass: true },
    { num: 10, name: 'Phase 2 orientation', pass: true },
    { num: 11, name: 'ADR generation', pass: true },
  ];

  return (
    <FadeIn className="mt-24">
      <div className="text-center mb-12">
        <h2 className="section-heading mb-4">
          Session <span className="text-gradient">Results</span>
        </h2>
        <p className="section-subheading mx-auto">
          Every step passed. Every response was shaped by the project&apos;s guardrails.
        </p>
      </div>

      <div className="max-w-2xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {steps.map((step) => (
            <div
              key={step.num}
              className="flex items-center gap-3 px-4 py-3 rounded-lg bg-surface-50/50 border border-dark-border"
            >
              <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                <Check className="h-3.5 w-3.5 text-green-400" />
              </div>
              <div>
                <span className="text-xs text-gray-500">Step {step.num}</span>
                <div className="text-sm text-white">{step.name}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20">
            <CheckCircle2 className="h-4 w-4 text-green-400" />
            <span className="text-sm font-semibold text-green-400">11 / 11 passed</span>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            8 documentation files generated across 2 phases in a single session
          </p>
        </div>
      </div>
    </FadeIn>
  );
}

/* ------------------------------------------------------------------ */
/*  Bottom CTA                                                         */
/* ------------------------------------------------------------------ */
function BottomCTA() {
  return (
    <section className="relative py-32">
      <div className="absolute inset-0 bg-gradient-to-t from-accent/[0.04] to-transparent" />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <FadeIn>
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6">
            Give your team this{' '}
            <span className="text-gradient">experience</span>
          </h2>
          <p className="text-lg text-gray-400 mb-10 max-w-2xl mx-auto">
            Generate a project with guardrails, playbooks, and slash commands —
            so every developer&apos;s first day looks like this.
          </p>
          <Link to="/wizard" className="btn-primary text-lg px-10 py-4">
            Build Your Blueprint
            <ArrowRight className="h-5 w-5" />
          </Link>
        </FadeIn>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Page                                                          */
/* ------------------------------------------------------------------ */
export default function ExampleSessionPage() {
  return (
    <div className="relative min-h-screen pt-28 pb-20">
      {/* Background effects */}
      <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-20" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-accent/5 rounded-full blur-[120px]" />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <HeroHeader />
        <SessionContext />

        {/* ============================================================ */}
        {/* ACT 1: First Contact                                         */}
        {/* ============================================================ */}
        <ActHeader
          actNumber={1}
          title="First Contact"
          description="The developer just cloned the repo and has no idea what it is."
          icon={MessageSquare}
        />

        <StepCard
          stepNumber={1}
          title="What is this?"
          developerSays="I just cloned this repo and I have no idea what it is. Can you tell me what this project is about and how I should get started?"
          claudeResponse={
            <>
              <p><strong className="text-white">TaskFlow</strong> is a team task management platform designed for remote engineering teams. It&apos;s built with <strong className="text-white">TypeScript + Next.js</strong>, <strong className="text-white">PostgreSQL</strong> database, <strong className="text-white">Vitest</strong> for testing, and <strong className="text-white">GitHub Actions</strong> for CI/CD.</p>
              <p className="mt-2">This appears to be a <strong className="text-white">greenfield project</strong> (newly scaffolded). It&apos;s in <strong className="text-white">Phase 01: Discovery & Requirements</strong> of a structured development playbook. The project structure is set up but core application code hasn&apos;t been written yet.</p>
              <p className="mt-2">Since the project is in the Discovery phase, you should focus on: reviewing or creating requirements documents, understanding stakeholder needs, defining user stories. <strong className="text-white">No application code should be written yet</strong> until requirements are finalized.</p>
            </>
          }
          guardrail={{
            title: 'CLAUDE.md Context',
            description: 'Claude identified the tech stack, phase, and project purpose from CLAUDE.md — loaded automatically when Claude Code starts.',
            without: 'Generic response like "I see a Node.js project with some config files." No awareness of the playbook, tech stack, or project goals.',
            color: 'accent',
          }}
        />

        <StepCard
          stepNumber={2}
          title="What commands are available?"
          developerSays="What slash commands are available in this project? I want to know what tools I have."
          claudeResponse={
            <>
              <p className="font-semibold text-white mb-2">Phase 01: Discovery & Requirements (Current Phase)</p>
              <div className="text-xs text-gray-400 space-y-0.5 mb-3">
                <div><code className="text-accent">/01-gather-requirements</code> — Interactive prompt to extract and structure requirements</div>
                <div><code className="text-accent">/01-user-stories</code> — Convert requirements into well-structured user stories</div>
                <div><code className="text-accent">/01-nfr-checklist</code> — Identify non-functional requirements across quality attributes</div>
              </div>
              <p className="font-semibold text-white mb-2">Phase 02-07: Architecture through Launch</p>
              <div className="text-xs text-gray-400 space-y-0.5 mb-3">
                <div><code className="text-accent">/02-api-design</code>, <code className="text-accent">/02-data-model-design</code>, <code className="text-accent">/02-adr-generator</code></div>
                <div><code className="text-accent">/03-project-scaffold</code>, <code className="text-accent">/03-ci-pipeline</code>, <code className="text-accent">/03-iac-setup</code></div>
                <div>... 12 more phase-specific commands</div>
              </div>
              <p className="font-semibold text-white mb-2">Core Workflow Commands</p>
              <div className="text-xs text-gray-400 space-y-0.5">
                <div><code className="text-accent">/plan</code> — Create an implementation plan (wait for confirmation before coding)</div>
                <div><code className="text-accent">/tdd</code> — Test-driven development session</div>
                <div><code className="text-accent">/code-review</code> — Review uncommitted changes</div>
                <div><code className="text-accent">/verify</code> — Full verification: build, types, lint, tests</div>
                <div><code className="text-accent">/phase</code> — Manage playbook phases (status, advance, rewind)</div>
              </div>
              <p className="text-xs text-gray-500 mt-3">You&apos;re currently in Phase 01, so the /01-* commands are most relevant right now.</p>
            </>
          }
          guardrail={{
            title: 'Slash Command Discovery',
            description: '27 commands auto-discovered from .claude/commands/ — each with a description in YAML frontmatter. No memorization needed.',
            without: '"I don\'t see any project-specific commands." Developer would need to manually browse playbook/prompts/ to find templates.',
            color: 'purple',
          }}
        />

        {/* ============================================================ */}
        {/* ACT 2: Discovering the Playbook                               */}
        {/* ============================================================ */}
        <ActHeader
          actNumber={2}
          title="Discovering the Playbook"
          description="The developer learns about the phase system and what they should do first."
          icon={Layers}
        />

        <StepCard
          stepNumber={3}
          title="Phase status"
          developerSays="/phase status"
          claudeResponse={
            <>
              <p className="font-semibold text-white">Phase Status: Discovery & Requirements</p>
              <p className="text-xs text-gray-500 mt-1">Current Phase: 1 of 7 &middot; Status: Active</p>
              <div className="mt-3 space-y-3 text-xs text-gray-400">
                <div>
                  <p className="font-semibold text-gray-300 mb-1">Objectives</p>
                  <div className="space-y-0.5">
                    <div>- Identify all stakeholders and their needs</div>
                    <div>- Document functional and non-functional requirements</div>
                    <div>- Define success criteria and KPIs</div>
                    <div>- Create user stories or use cases</div>
                  </div>
                </div>
                <div>
                  <p className="font-semibold text-gray-300 mb-1">Deliverables</p>
                  <div className="space-y-0.5">
                    <div>1. <strong className="text-white">Requirements Document</strong> — Comprehensive specification of all requirements</div>
                    <div>2. <strong className="text-white">Stakeholder Map</strong> — Identified stakeholders with roles and communication plan</div>
                  </div>
                </div>
                <div>
                  <p className="font-semibold text-gray-300 mb-1">Quality Gates</p>
                  <div className="space-y-0.5">
                    <div>- All stakeholders have reviewed and signed off on requirements</div>
                    <div>- Success criteria are measurable and agreed upon</div>
                    <div>- No ambiguous or conflicting requirements remain</div>
                  </div>
                </div>
              </div>
            </>
          }
          guardrail={{
            title: 'Phase Awareness',
            description: 'Phase state from current-phase.json gives structured objectives, deliverables, and quality gates — a clear definition of done.',
            without: 'No concept of what phase you\'re in, what you should deliver, or when you\'re ready to move on.',
            color: 'green',
          }}
        />

        <StepCard
          stepNumber={4}
          title="What should I do first?"
          developerSays="OK so I'm on Phase 1: Discovery. What exactly should I do? What are my options? I'm building TaskFlow for my team of 3 developers."
          claudeResponse={
            <>
              <p>You have three specialized skills available in this phase:</p>
              <ol className="list-decimal list-inside text-xs text-gray-400 space-y-2 mt-2">
                <li><code className="text-accent">/01-gather-requirements</code> — Interactive session to extract and structure requirements from your business description</li>
                <li><code className="text-accent">/01-nfr-checklist</code> — Systematically identify non-functional requirements (performance, security, scalability)</li>
                <li><code className="text-accent">/01-user-stories</code> — Convert requirements into well-structured user stories with acceptance criteria</li>
              </ol>
              <p className="mt-3">By the end of Phase 01, you should have:</p>
              <div className="text-xs text-gray-400 mt-1 space-y-0.5">
                <div>- <strong className="text-white">Requirements Document</strong> — Comprehensive specification of all requirements</div>
                <div>- <strong className="text-white">Stakeholder Map</strong> — Identified stakeholders (your 3 developers + yourself)</div>
              </div>
              <p className="text-xs text-gray-500 mt-3">I recommend beginning with <code className="text-accent">/01-gather-requirements</code> to capture what TaskFlow needs to do for your team.</p>
            </>
          }
          guardrail={{
            title: 'Phase-Scoped Guidance',
            description: 'Claude restricts recommendations to Phase 1 activities only. Won\'t suggest jumping to architecture or coding.',
            without: 'Might suggest "Let\'s start building the API" or "Set up the database first" — skipping the requirements phase entirely.',
            color: 'amber',
          }}
        />

        {/* ============================================================ */}
        {/* ACT 3: Getting to Work                                        */}
        {/* ============================================================ */}
        <ActHeader
          actNumber={3}
          title="Getting to Work"
          description="The developer runs Phase 1 commands and produces real deliverables."
          icon={FileText}
        />

        <StepCard
          stepNumber={5}
          title="Gather requirements"
          developerSays="/01-gather-requirements — We're building TaskFlow, a task management platform for remote engineering teams. Key features: real-time task collaboration, sprint planning with velocity tracking, time tracking per task, GitHub PR linking, and Slack notifications. Team of 3 devs, 4-month timeline, SOC2 and GDPR compliance required. Write the output to docs/requirements.md"
          claudeResponse={
            <>
              <p>I&apos;ve created a comprehensive requirements document for TaskFlow at <code className="text-accent">docs/requirements.md</code>.</p>
              <p className="mt-2 font-semibold text-white">26 Functional Requirements organized into 8 categories:</p>
              <div className="text-xs text-gray-400 mt-1 space-y-0.5">
                <div>Task Management (FR-001 to FR-005) &middot; Real-Time Collaboration (FR-006 to FR-007)</div>
                <div>Sprint Planning (FR-008 to FR-011) &middot; Time Tracking (FR-012 to FR-014)</div>
                <div>GitHub Integration (FR-015 to FR-017) &middot; Slack Integration (FR-018 to FR-020)</div>
                <div>User Management (FR-021 to FR-024) &middot; Search and Filtering (FR-025 to FR-026)</div>
              </div>
              <p className="mt-2 font-semibold text-white">15 Non-Functional Requirements across 6 categories:</p>
              <div className="text-xs text-gray-400 mt-1 space-y-0.5">
                <div>Performance (NFR-001 to NFR-004) &middot; Security (NFR-005 to NFR-009)</div>
                <div>Compliance (NFR-010 to NFR-012) — SOC2 & GDPR</div>
              </div>
              <p className="text-xs text-gray-400 mt-2">Each requirement includes unique ID, type classification, priority (Must/Should/Could), acceptance criteria, and measurable success metrics. Stakeholder map, constraints, and risk assessment included.</p>
            </>
          }
          guardrail={{
            title: 'Direct Mode',
            description: 'Developer provided context inline and Claude generated the deliverable immediately — no follow-up questions asked.',
            without: 'Would ask 8 categories of clarifying questions about roles, permissions, sprint duration, time tracking, etc. — producing nothing. (This happened in Runs 1-3 before the fix.)',
            color: 'accent',
          }}
        />

        <StepCard
          stepNumber={6}
          title="Generate user stories"
          developerSays="/01-user-stories — Based on the requirements in docs/requirements.md, generate user stories grouped by epic with acceptance criteria and t-shirt sizing. Write to docs/user-stories.md"
          claudeResponse={
            <>
              <p>I&apos;ve generated a comprehensive user stories document with <strong className="text-white">70 user stories</strong> organized into <strong className="text-white">14 epics</strong>.</p>
              <div className="mt-2 text-xs text-gray-400">
                <p className="font-semibold text-gray-300 mb-1">User Roles: Engineering Manager, Software Engineer, Product Manager, Administrator, Viewer</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-2">
                  <div>Task Management (7 stories)</div>
                  <div>Sprint Planning (5 stories)</div>
                  <div>Time Tracking (5 stories)</div>
                  <div>GitHub Integration (5 stories)</div>
                  <div>Slack Integration (6 stories)</div>
                  <div>User Management (7 stories)</div>
                  <div>Security & Compliance (6 stories)</div>
                  <div>Reporting & Analytics (3 stories)</div>
                  <div className="col-span-2">+ 6 more epics...</div>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2">T-shirt sizing: 3 XS, 20 S, 37 M, 7 L. All stories follow &quot;As a [role], I want [capability], so that [benefit]&quot; format with acceptance criteria. Implementation priority roadmap provided across 5 phases.</p>
            </>
          }
          guardrail={{
            title: 'Personalized Templates',
            description: 'Prompt template pre-filled with project name, tech stack, and description. Stories reference TaskFlow-specific features, not generic placeholders.',
            without: 'Generic user stories for "the application" with no reference to real-time collaboration, GitHub integration, or compliance requirements.',
            color: 'green',
          }}
        />

        <StepCard
          stepNumber={7}
          title="NFR checklist"
          developerSays="/01-nfr-checklist — Generate non-functional requirements for TaskFlow. Key targets: 99.9% uptime, sub-200ms p95 API response, 500 initial users scaling to 2000, SOC2 and GDPR compliance, WCAG 2.1 AA accessibility. Write to docs/nfr.md"
          claudeResponse={
            <>
              <p>I&apos;ve created a comprehensive Non-Functional Requirements document at <code className="text-accent">docs/nfr.md</code>.</p>
              <div className="mt-2 space-y-1.5 text-xs text-gray-400">
                <div><strong className="text-white">Performance</strong> — API response: p50 &lt;100ms, p95 &lt;200ms, p99 &lt;500ms. 500 RPS sustained, 1,000 burst.</div>
                <div><strong className="text-white">Scalability</strong> — 500 initial users scaling to 2,000 with capacity to 5,000. Horizontal auto-scaling.</div>
                <div><strong className="text-white">Availability</strong> — 99.9% uptime SLA (43.2 min downtime/month). RTO 4h, RPO 1h.</div>
                <div><strong className="text-white">Security</strong> — SOC2 Type II compliance. GDPR with Right to Access, Erasure, Portability. OWASP Top 10.</div>
                <div><strong className="text-white">Accessibility</strong> — WCAG 2.1 Level AA. Keyboard navigation, 4.5:1 contrast, screen reader support.</div>
                <div><strong className="text-white">Maintainability</strong> — 80%+ test coverage. Structured logging. OpenTelemetry tracing.</div>
              </div>
              <p className="text-xs text-gray-500 mt-2">All requirements measurable and testable. Industry benchmarks referenced: Google SRE, AWS Well-Architected, NIST, OWASP.</p>
            </>
          }
          guardrail={{
            title: 'Compliance Awareness',
            description: 'NFR template resolves {{complianceFrameworks}} to "SOC2, GDPR" because the wizard captured compliance requirements during project setup.',
            without: 'Compliance requirements might not be mentioned at all, or only as a vague afterthought.',
            color: 'red',
          }}
        />

        {/* ============================================================ */}
        {/* ACT 4: Progress Check                                         */}
        {/* ============================================================ */}
        <ActHeader
          actNumber={4}
          title="Progress Check"
          description="Before moving on, the developer checks if Phase 1 deliverables are complete."
          icon={Shield}
        />

        <StepCard
          stepNumber={8}
          title="Quality gate assessment"
          developerSays="/phase check"
          claudeResponse={
            <>
              <p className="font-semibold text-white">Phase Check: Discovery & Requirements (Phase 01 of 7)</p>
              <div className="mt-3 space-y-2 text-xs">
                <div className="flex items-start gap-2">
                  <span className="text-green-400 shrink-0">&#10003;</span>
                  <span className="text-gray-400"><strong className="text-white">Requirements Document</strong> (955 lines) — FR-001 through FR-044, stakeholder map, success metrics, risk analysis</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-400 shrink-0">&#10003;</span>
                  <span className="text-gray-400"><strong className="text-white">Stakeholder Map</strong> — 5 stakeholders identified with roles and communication frequency</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-400 shrink-0">&#10003;</span>
                  <span className="text-gray-400"><strong className="text-white">User Stories</strong> (1,305 lines) — 50+ stories across 9 epics with acceptance criteria</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-400 shrink-0">&#10003;</span>
                  <span className="text-gray-400"><strong className="text-white">NFR Checklist</strong> (889 lines) — 8 quality dimensions with measurable targets</span>
                </div>
              </div>
              <div className="mt-3 space-y-1.5 text-xs text-gray-400">
                <div><span className="text-green-400">&#10003;</span> Success criteria are measurable and agreed upon — <span className="text-green-400">FULLY MET</span></div>
                <div><span className="text-green-400">&#10003;</span> No ambiguous or conflicting requirements — <span className="text-green-400">FULLY MET</span></div>
                <div><span className="text-amber-400">~</span> Stakeholder sign-off — <span className="text-amber-400">PARTIALLY MET</span> (sign-off table exists, signatures pending)</div>
              </div>
              <p className="text-green-400 font-semibold mt-3 text-xs">Phase 01 is READY to advance to Phase 02: Architecture & Design.</p>
            </>
          }
          guardrail={{
            title: 'Quality Gates',
            description: 'Phase check cross-references actual files in docs/ against the phase\'s required deliverables. Reports line counts, coverage, and missing items.',
            without: 'No structured way to assess completeness. Developer would guess whether they\'re "done enough" to move on.',
            color: 'green',
          }}
        />

        {/* ============================================================ */}
        {/* ACT 5: Phase Transition                                       */}
        {/* ============================================================ */}
        <ActHeader
          actNumber={5}
          title="Phase Transition"
          description="The developer advances to Phase 2 and starts architecture work."
          icon={Zap}
        />

        <StepCard
          stepNumber={9}
          title="Advance to Phase 2"
          developerSays="/phase next"
          claudeResponse={
            <>
              <p className="font-semibold text-white">Successfully advanced to Phase 02: Architecture & Design.</p>
              <p className="text-xs text-gray-500 mt-1">Phase 2 of 7 &middot; Started: 2026-02-17</p>
              <div className="mt-3 space-y-2 text-xs text-gray-400">
                <div>
                  <p className="font-semibold text-gray-300 mb-1">Objectives</p>
                  <div className="space-y-0.5">
                    <div>- Select appropriate technology stack with documented rationale</div>
                    <div>- Design system architecture with component boundaries and data flows</div>
                    <div>- Create Architecture Decision Records for all significant choices</div>
                    <div>- Define API contracts and data models</div>
                  </div>
                </div>
                <div>
                  <p className="font-semibold text-gray-300 mb-1">Available Skills</p>
                  <div className="space-y-0.5">
                    <div><code className="text-accent">/02-adr-generator</code> — Create well-structured ADRs for technical decisions</div>
                    <div><code className="text-accent">/02-api-design</code> — Design RESTful or GraphQL APIs from requirements</div>
                    <div><code className="text-accent">/02-data-model-design</code> — Design database schemas from domain requirements</div>
                  </div>
                </div>
              </div>
            </>
          }
          guardrail={{
            title: 'Imperative Execution',
            description: 'The /phase next command executes the transition script immediately — no confirmation dialog. The developer said "next", so we move.',
            without: 'Claude describes what would happen but doesn\'t actually run the script. Developer stays stuck on Phase 1. (This happened in Runs 1-4 before the fix.)',
            color: 'accent',
          }}
        />

        <StepCard
          stepNumber={10}
          title="Phase 2 orientation"
          developerSays="/phase status"
          claudeResponse={
            <>
              <p className="font-semibold text-white">Phase Status: Architecture & Design</p>
              <p className="text-xs text-gray-500 mt-1">Phase 2 of 7 &middot; Status: Active</p>
              <div className="mt-3 space-y-3 text-xs text-gray-400">
                <div>
                  <p className="font-semibold text-gray-300 mb-1">Deliverables</p>
                  <div className="space-y-0.5">
                    <div>1. <strong className="text-white">Architecture Decision Records</strong> — Technology choices, patterns, trade-offs</div>
                    <div>2. <strong className="text-white">System Architecture Diagram</strong> — Services, data stores, integrations</div>
                    <div>3. <strong className="text-white">API Specification</strong> — OpenAPI or GraphQL schema</div>
                    <div>4. <strong className="text-white">Data Model</strong> — ER diagram and schema definitions</div>
                  </div>
                </div>
                <div>
                  <p className="font-semibold text-gray-300 mb-1">Quality Gates</p>
                  <div className="space-y-0.5">
                    <div>- All major technology decisions have corresponding ADRs</div>
                    <div>- Architecture supports all documented non-functional requirements</div>
                    <div>- API contracts reviewed and agreed upon</div>
                    <div>- Security architecture review completed</div>
                  </div>
                </div>
              </div>
            </>
          }
          guardrail={{
            title: 'Phase Transition',
            description: 'Phase 2 is now active with completely new objectives, deliverables, quality gates, and slash commands. Claude\'s behavior adapts automatically.',
            without: 'Still showing Phase 1. No concept of progression or what changes when you advance.',
            color: 'green',
          }}
        />

        <StepCard
          stepNumber={11}
          title="Architecture Decision Record"
          developerSays="/02-adr-generator — Generate an ADR for TaskFlow's backend tech stack. Options: (A) Next.js full-stack + Prisma + PostgreSQL, (B) Express.js + TypeORM + PostgreSQL, (C) Fastify + Drizzle + PostgreSQL. Team knows React/TypeScript, needs WebSocket support, SOC2 required, 4-month timeline. Write to docs/adr/001-tech-stack.md"
          claudeResponse={
            <>
              <p>I&apos;ve generated ADR-001 for TaskFlow&apos;s backend tech stack at <code className="text-accent">docs/adr/001-tech-stack.md</code>.</p>
              <p className="mt-2 font-semibold text-white">Decision: Next.js Full-Stack + Prisma + PostgreSQL (Option A)</p>
              <div className="mt-2 space-y-1.5 text-xs text-gray-400">
                <div><strong className="text-white">Score: 4.2/5</strong> — Best for team expertise alignment and development velocity</div>
                <div><strong className="text-gray-300">Performance</strong> — 12k RPS vs 500 RPS requirement = 24x headroom</div>
                <div><strong className="text-gray-300">Cost</strong> — $250/month for 500 users (50% under budget)</div>
                <div><strong className="text-gray-300">Timeline</strong> — 2-4 weeks faster to MVP than alternatives</div>
                <div><strong className="text-gray-300">WebSocket</strong> — Mitigated via separate Socket.io server ($30/month)</div>
                <div><strong className="text-gray-300">SOC2</strong> — Fully compliant with Prisma middleware for audit logging</div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Includes quantitative comparison of all 3 options, risk assessment with mitigation strategies, and implementation timeline.</p>
            </>
          }
          guardrail={{
            title: 'Cross-Phase Continuity',
            description: 'The ADR references requirements and NFRs from Phase 1 docs — compliance targets, performance targets, and team constraints are carried forward.',
            without: 'Starts from scratch with no awareness of the 3,000+ lines of requirements already produced.',
            color: 'purple',
          }}
        />

        {/* ============================================================ */}
        {/* Scoreboard                                                    */}
        {/* ============================================================ */}
        <Scoreboard />
      </div>

      <BottomCTA />
    </div>
  );
}
